import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Compass, RotateCcw, Rocket, X, MailX, LogOut, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Lock, Check, Info, MoreHorizontal } from "lucide-react";
import { useMenuA11y } from "../lib/menu";
import { Btn, Card, Tag, cx } from "./kit";
import { ConfirmDialog } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../lib/auth";
import { TOKEN_KEY } from "../lib/api";
import { DEMO_ROLES, getDemo, patchDemo, resetDemo, switchDemoRole, onSimulated, clearDemo } from "../lib/demo";

const STEPS = [
  { key: "welcome", role: "groomer", to: () => "/home" },
  { key: "dog", role: "groomer", to: (ids) => `/dogs/${ids.dog}` },
  { key: "programme", role: "groomer", to: (ids) => `/programmes/${ids.programme}` },
  { key: "visit", role: "groomer", to: (ids) => `/visits/${ids.visit}` },
  { key: "send", role: "groomer", to: (ids) => `/programmes/${ids.programme}` },
  { key: "owner", role: "owner", to: () => "/owner" },
  { key: "review", role: "groomer", to: () => "/home" },
  { key: "review2", ns: "demo2", k: "review", role: "groomer", to: (ids) => `/programmes/${ids.programme}` },
  { key: "consult", ns: "demo2", k: "consult", role: "groomer", to: (ids) => `/programmes/${ids.history_programme || ids.programme}` },
  { key: "timeline", ns: "demo2", k: "timeline", role: "groomer", to: (ids) => `/dogs/${ids.history_dog}/timeline` },
  { key: "calendar", ns: "demo2", k: "calendar", role: "groomer", to: () => "/calendar" },
  { key: "conversation", ns: "demo2", k: "conversation", role: "groomer", to: (ids) => `/messages/${ids.conversation}` },
  { key: "learning", ns: "demo3", k: "learning", role: "groomer", to: () => "/learning" },
  { key: "mentor_queue", ns: "demo3", k: "queue", role: "mentor", to: () => "/mentoring/queue" },
  { key: "mentor_review", ns: "demo3", k: "review", role: "mentor", to: (ids) => `/learning/practicals/${ids.practical}` },
  { key: "learner_back", ns: "demo3", k: "back", role: "groomer", to: (ids) => `/learning/practicals/${ids.practical}` },
  { key: "catalogue", ns: "demo4", k: "catalogue", role: "groomer", to: () => "/products" },
  { key: "substitute", ns: "demo4", k: "substitute", role: "groomer", to: (ids) => `/visits/${ids.visit_sub || ids.visit}` },
  { key: "my_use", ns: "demo4", k: "my_use", role: "groomer", to: () => "/products" },
  { key: "docs", ns: "demo5", k: "docs", role: "groomer", to: () => "/profile" },
  { key: "cases", ns: "demo5", k: "cases", role: "groomer", to: () => "/cases" },
  { key: "end" },
];
const lng = (i18n) => (i18n.language?.startsWith("ru") ? "ru" : "en");

const exitDemo = () => { clearDemo(); localStorage.removeItem(TOKEN_KEY); window.location.assign("/"); };

export function useStartRealWork() {
  return () => { clearDemo(); localStorage.removeItem(TOKEN_KEY); window.location.assign("/register"); };
}

function Drop({ testid, label, button, children, className = "", panelClass = "" }) {
  const [open, setOpen] = useState(false);
  const bref = useRef(null), pref = useRef(null);
  useMenuA11y(open, () => setOpen(false), bref, pref);
  return (
    <div className={cx("relative", className)} data-testid={testid}>
      <button ref={bref} type="button" aria-haspopup="true" aria-expanded={open} aria-label={label} data-testid={`${testid}-toggle`} onClick={() => setOpen(!open)}
        className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-ink/15 bg-white/80 px-3 text-sm font-semibold hover:bg-white">{button}</button>
      {open && <><div className="fixed inset-0 z-[55]" aria-hidden onClick={() => setOpen(false)} />
        <div ref={pref} role="menu" className={cx("absolute left-0 top-full z-[60] mt-2 w-72 max-w-[calc(100vw-24px)] rounded-2xl border bg-card p-2 text-ink shadow-lift", panelClass)} onClick={(e) => e.target.closest("button,a") && setOpen(false)}>{children}</div></>}
    </div>
  );
}

function RoleSwitcher() {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const cur = getDemo()?.role || "groomer";
  const go = async (r) => {
    if (r.key === cur) return;
    switchDemoRole(r.key);
    await refresh();
    navigate(r.path);
  };
  return (
    <Drop testid="demo-role-switcher" label={t("sandbox.role_label")} button={<><span className="hidden sm:inline" data-testid="demo-current-role">{t("b5.demo_role", { role: t(`sandbox.roles.${cur}`) })}</span><span className="max-w-[84px] truncate sm:hidden">{t(`sandbox.roles.${cur}`)}</span><ChevronDown className="h-4 w-4" aria-hidden /></>}>
      <p className="px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("sandbox.role_label")}</p>
      {DEMO_ROLES.map((r) => (
        <button key={r.key} role="menuitemradio" data-testid={`demo-role-${r.key}`} disabled={!r.live} aria-checked={cur === r.key} aria-pressed={cur === r.key} onClick={() => go(r)}
          className={cx("flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 text-left text-sm font-semibold", cur === r.key ? "bg-sand" : r.live ? "hover:bg-sand" : "cursor-not-allowed text-muted-foreground")}>
          {cur === r.key ? <Check className="h-4 w-4 text-clay" aria-hidden /> : r.live ? <span className="w-4" /> : <Lock className="h-4 w-4" aria-hidden />}
          <span className="mr-auto">{t(`sandbox.roles.${r.key}`)}</span>{!r.live && <span className="text-xs font-normal">{t("sandbox.coming_later")}</span>}
        </button>
      ))}
    </Drop>
  );
}

function SimulatedDialog() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  useEffect(() => onSimulated(setItems), []);
  return (
    <Dialog open={!!items} onOpenChange={(o) => !o && setItems(null)}>
      <DialogContent data-testid="simulated-email-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MailX className="h-5 w-5" aria-hidden />{t("sandbox.simulated_title")}</DialogTitle>
          <DialogDescription>{t("sandbox.simulated_sub")}</DialogDescription>
        </DialogHeader>
        {(items || []).map((p, i) => (
          <div key={i} data-testid={`simulated-preview-${i}`} lang={p.language} className="rounded-xl border bg-muted/40 p-3 text-sm">
            <p className="text-xs text-muted-foreground">{t("confirm.recipient")}: {p.to} · {p.language?.toUpperCase()}</p>
            <p className="mt-1 font-semibold">{p.subject}</p>
            <p className="mt-1 whitespace-pre-wrap">{p.body}</p>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function Guide({ onClose }) {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const startReal = useStartRealWork();
  const [step, setStep] = useState(getDemo()?.step || 0);
  const [min, setMin] = useState(!!getDemo()?.guide_min);
  const s = STEPS[step];
  const move = (n) => { setStep(n); patchDemo({ step: n }); };
  const minimise = (v) => { setMin(v); patchDemo({ guide_min: v }); };
  useEffect(() => {
    const c = document.documentElement.classList;
    c.toggle("demo-guide-on", !min);
    c.toggle("demo-guide-min", min);
    return () => c.remove("demo-guide-on", "demo-guide-min");
  }, [min]);
  const go = async () => {
    const d = getDemo();
    if (s.role && d.role !== s.role) { switchDemoRole(s.role); await refresh(); }
    navigate(s.to(d.ids));
  };
  if (min) {
    return (
      <button data-testid="demo-guide-expand" onClick={() => minimise(false)} aria-label={t("sandbox.expand")}
        className="fixed right-0 top-1/2 z-50 flex min-h-[48px] -translate-y-1/2 items-center gap-1.5 rounded-l-full bg-ink py-2 pl-3 pr-2 text-sm font-semibold text-white shadow-xl sm:bottom-5 sm:right-5 sm:top-auto sm:translate-y-0 sm:rounded-full sm:px-4">
        <Compass className="h-4 w-4 shrink-0" aria-hidden />
        <span className="sm:hidden">{step + 1}/{STEPS.length}</span>
        <span className="hidden max-w-[300px] truncate sm:inline">{t("sandbox.step_of", { n: step + 1, total: STEPS.length })} · {t(`${s.ns || "sandbox.steps"}.${s.k || s.key}.title`)}</span>
        <ChevronUp className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden />
      </button>
    );
  }
  return (
    <Card data-testid="demo-guide-panel" role="complementary" aria-label={t("sandbox.guide")}
      className="fixed inset-x-2 bottom-[64px] z-30 flex max-h-[25vh] flex-col p-3 shadow-xl lg:bottom-2 xl:inset-x-auto xl:bottom-auto xl:right-4 xl:top-[136px] xl:max-h-[calc(100vh-152px)] xl:w-[360px] xl:p-4">
      <ol className="mb-2 flex gap-1" aria-hidden>{STEPS.map((_, i) => <li key={i} className={cx("h-1 flex-1 rounded-full", i < step ? "bg-clay/50" : i === step ? "bg-clay" : "bg-muted")} />)}</ol>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="demo-guide-progress">{t("sandbox.step_of", { n: step + 1, total: STEPS.length })}</p>
        <div className="-mr-1 -mt-1 flex">
          <button data-testid="demo-guide-minimise" onClick={() => minimise(true)} aria-label={t("sandbox.minimise")} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"><ChevronDown className="h-4 w-4" /></button>
          <button data-testid="demo-guide-close" onClick={onClose} aria-label={t("sandbox.guide_close")} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="demo-guide-scroll">
      <h2 className="mt-0.5 font-display text-base font-bold sm:text-lg" data-testid="demo-guide-title">{t(`${s.ns || "sandbox.steps"}.${s.k || s.key}.title`)}</h2>
      <p className="mt-1 text-sm" data-testid="demo-guide-body">{t(`${s.ns || "sandbox.steps"}.${s.k || s.key}.body`)}</p>
      {s.key !== "end" && <p className="mt-2 rounded-lg bg-lavender p-2 text-xs" data-testid="demo-guide-consequence"><b>{t("sandbox.then")}</b> {t(`${s.ns || "sandbox.steps"}.${s.k || s.key}.then`)}</p>}
      {s.key === "end" && (
        <div className="mt-3 space-y-2">
          <div data-testid="pricing-coming-later-card" className="rounded-xl border border-dashed p-3 text-sm"><Tag>{t("sandbox.coming_later")}</Tag><p className="mt-1 font-semibold">{t("sandbox.pricing")}</p></div>
          <Btn testid="guide-start-real-work-btn" className="w-full" onClick={startReal}><Rocket className="h-4 w-4" />{t("sandbox.start_real")}</Btn>
        </div>
      )}
      </div>
      <div className="mt-2 flex shrink-0 items-center gap-2">
        <Btn variant="outline" testid="demo-guide-back" className="min-h-[40px] px-3" disabled={step === 0} onClick={() => move(step - 1)} aria-label={t("common.back")}><ChevronLeft className="h-4 w-4" /></Btn>
        {s.to && <Btn variant="outline" testid="demo-guide-go" className="min-h-[40px] flex-1" onClick={go}>{t("sandbox.go_there")}</Btn>}
        {step < STEPS.length - 1 && <Btn testid="demo-guide-next" className="min-h-[40px] flex-1" onClick={() => move(step + 1)}>{t("sandbox.next")}<ChevronRight className="h-4 w-4" /></Btn>}
      </div>
    </Card>
  );
}

export default function DemoChrome() {
  const { t, i18n } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const startReal = useStartRealWork();
  const [guide, setGuide] = useState(getDemo()?.guide !== false);
  const [confirm, setConfirm] = useState(false);
  const toggle = (v) => { setGuide(v); patchDemo({ guide: v }); };
  const reset = async () => { await resetDemo(lng(i18n)); await refresh(); setGuide(true); navigate("/home"); };
  return (
    <>
      <div data-testid="demo-banner" role="region" aria-label={t("sandbox.banner_label")} className="border-b bg-amber text-ink">
        <div className="mx-auto flex min-h-[48px] max-w-6xl items-center gap-1.5 px-2 py-1 sm:gap-2 sm:px-6">
          <span className="rounded-md bg-ink px-2 py-0.5 text-xs font-extrabold uppercase text-white" data-testid="demo-status">{t("b5.demo_status")}</span>
          <RoleSwitcher />
          <Drop testid="demo-info" label={t("b9.demo_info")} className="hidden sm:block" button={<Info className="h-4 w-4" aria-hidden />} panelClass="w-80">
            <div className="space-y-2 p-2 text-sm"><p className="font-bold" data-testid="demo-banner-text">{t("sandbox.banner")}</p><p className="text-xs" data-testid="demo-reset-policy">{t("sandbox.reset_policy")}</p></div>
          </Drop>
          <button type="button" data-testid="demo-guide-open" aria-pressed={guide} onClick={() => toggle(!guide)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-ink/15 bg-white/80 px-3 text-sm font-semibold hover:bg-white" aria-label={t("sandbox.guide")}>
            <Compass className="h-4 w-4" aria-hidden /><span className="hidden lg:inline">{t("sandbox.guide")}</span></button>
          <div className="ml-auto flex items-center gap-2">
            <Drop testid="demo-more" label={t("b9.more")} button={<MoreHorizontal className="h-4 w-4" aria-hidden />} panelClass="left-auto right-0 w-60">
              <div className="p-1 text-sm sm:hidden"><p className="font-bold">{t("sandbox.banner")}</p><p className="mt-1 text-xs">{t("sandbox.reset_policy")}</p></div>
              <button role="menuitem" data-testid="demo-reset-btn" onClick={() => setConfirm(true)} className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 text-sm font-semibold hover:bg-sand"><RotateCcw className="h-4 w-4" aria-hidden />{t("sandbox.reset")}</button>
              <button role="menuitem" data-testid="demo-exit-btn" onClick={exitDemo} className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 text-sm font-semibold hover:bg-sand"><LogOut className="h-4 w-4" aria-hidden />{t("b5.exit_demo")}</button>
            </Drop>
            <Btn testid="demo-start-real-work-btn" className="min-h-[40px] px-3 text-sm" onClick={startReal}><Rocket className="h-4 w-4" aria-hidden /><span className="hidden sm:inline">{t("sandbox.start_real")}</span><span className="max-[380px]:sr-only sm:hidden">{t("b9.start_short")}</span></Btn>
          </div>
        </div>
      </div>
      {guide && <Guide onClose={() => toggle(false)} />}
      <SimulatedDialog />
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={t("sandbox.reset_confirm_title")} consequence={t("sandbox.reset_confirm_body")} danger confirmLabel={t("sandbox.reset")} onConfirm={reset} testid="demo-reset-confirm" />
    </>
  );
}
